import React, { useContext, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import axios from '../../../../config/axios'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { Eye, Pencil, Trash2 } from 'lucide-react'
import PricesDisplay from '../../../../components/PricesDisplay'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import { secondsToHHmm } from '../../../../utils/time'
import ApiService from '../../../../services/ApiService'
import InfiniteSelect from '../../../../components/InfiniteSelect'
import { StandardColumn, StandardDataTable, useColumnsState } from '../../../../components/StandardPage'
import { OverflowCell } from '../../../../components/OverflowCell'

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: auto;
  width: 100%;
  padding-right: 10px;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #c0c0c0;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ffffff;
    border-radius: 10px;
    border: 2px solid #c0c0c0;
  }
`

interface Service {
  id: number
  name: string
  code?: string
  description?: string | null
  observation?: string | null
  category?: { id: number; name: string } | null
  type?: { id: number; name: string } | null
  sector?: { id: number; name: string; Area?: { id: number; name: string } | null } | null
  pricePerHour?: number
  prices?: Array<{
    id: number
    price: string
    unitOfMeasurement?: {
      id: number
      name: string
    }
    UnitOfMeasurement?: {
      id: number
      name: string
    }
  }>
  deadlineInDays?: number
  executionTime?: number | null
  setupTime?: number | null
  costs?: Array<{
    id: number
    cost: string
    unitOfMeasurementId?: number
    unitOfMeasurement?: {
      id: number
      name: string
    }
    UnitOfMeasurement?: {
      id: number
      name: string
    }
  }>
  markup?: string | null
  suppliers?: Array<{ id: number; companyName?: string; name?: string }>
  machines?: Array<{ id: number; name: string; model?: string }>
}

const servicesScreenKey = 'services'

const formatPrices = (prices?: any[]) => {
  if (!prices || prices.length === 0) return '-'
  return prices
    .map((price) => {
      const rawPrice = price.price ?? price.cost
      const num = typeof rawPrice === 'string' ? parseFloat(rawPrice) : Number(rawPrice)
      const formattedPrice = isNaN(num)
        ? rawPrice
        : new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }).format(num)
      const unit =
        price.UnitOfMeasurement?.name ||
        price.unitOfMeasurement?.name ||
        'UN'
      return `${formattedPrice}/${unit}`
    })
    .join('\n')
}

const getFirstWithEllipsis = (content: string) => {
  if (!content || content === '-') return '-'
  const parts = content
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
  if (parts.length === 0) return '-'
  if (parts.length === 1) return parts[0]
  return `${parts[0]}...`
}

const formatSuppliersSummary = (service: Service): string => {
  const sups = service.suppliers || (service as any).Company_Stage?.map((cs: any) => cs.Company) || []
  if (!sups || sups.length === 0) return '-'
  return sups.map((s: any) => s.companyName || s.name).filter(Boolean).join(', ') || '-'
}

const formatSuppliersTooltip = (service: Service): string => {
  const sups = service.suppliers || (service as any).Company_Stage?.map((cs: any) => cs.Company) || []
  if (!sups || sups.length === 0) return ''
  return sups.map((s: any) => s.companyName || s.name).filter(Boolean).join('\n')
}

const formatMachinesSummary = (service: Service): string => {
  const macs = service.machines || (service as any).Machine_Stage?.map((ms: any) => ms.Machine) || []
  if (!macs || macs.length === 0) return '-'
  return macs.map((m: any) => m.name).filter(Boolean).join(', ') || '-'
}

const formatMachinesTooltip = (service: Service): string => {
  const macs = service.machines || (service as any).Machine_Stage?.map((ms: any) => ms.Machine) || []
  if (!macs || macs.length === 0) return ''
  return macs.map((m: any) => m.name).filter(Boolean).join('\n')
}

const formatMarkup = (markup?: string | null) => {
  if (markup === null || markup === undefined || markup === '') return 'N/A'
  return `${Number(markup)}x`
}

const formatDeadline = (deadline?: number | null) => {
  if (!deadline) return 'N/A'
  return `${deadline} ${deadline === 1 ? 'dia útil' : 'dias úteis'}`
}

const formatTime = (value?: number | null) => {
  if (value == null) return 'N/A'
  const formatted = secondsToHHmm(value)
  return formatted ? formatted : 'N/A'
}

const buildServicesColumns = (): StandardColumn<Service>[] => [
  {
    id: 'name',
    label: 'Serviço',
  },
  {
    id: 'code',
    label: 'Código',
    accessor: (service) => service.code || 'N/A',
  },
  {
    id: 'area',
    label: 'Área',
    accessor: (service) => service.sector?.Area?.name || (service as any).Sector?.Area?.name || 'N/A',
  },
  {
    id: 'sector',
    label: 'Setor',
    accessor: (service) => service.sector?.name || (service as any).Sector?.name || 'N/A',
  },
  {
    id: 'category',
    label: 'Categoria',
    accessor: (service) => service.category?.name || (service as any).Category_Stage?.name || 'N/A',
  },
  {
    id: 'type',
    label: 'Tipo',
    accessor: (service) => service.type?.name || (service as any).Type_Category_Stage?.name || 'N/A',
  },
  {
    id: 'suppliers',
    label: 'Fornecedor',
    accessor: formatSuppliersSummary,
    cell: (service) => {
      const summary = formatSuppliersSummary(service)
      if (summary === '-') return '-'
      return (
        <OverflowCell text={formatSuppliersTooltip(service)} forceTooltip maxWidth={200}>
          {summary}
        </OverflowCell>
      )
    },
  },
  {
    id: 'machines',
    label: 'Máquinas',
    accessor: formatMachinesSummary,
    cell: (service) => {
      const summary = formatMachinesSummary(service)
      if (summary === '-') return '-'
      return (
        <OverflowCell text={formatMachinesTooltip(service)} forceTooltip maxWidth={200}>
          {summary}
        </OverflowCell>
      )
    },
  },
  {
    id: 'prices',
    label: 'Preço',
    sortable: false,
    cell: (service) => {
      const pList = service.prices || (service as any).StagePrice || []
      const formatted = formatPrices(pList)
      return (
        <OverflowCell
          text={formatted}
          forceTooltip={pList.length > 1}
          maxWidth={200}
        >
          {getFirstWithEllipsis(formatted)}
        </OverflowCell>
      )
    },
    exportValue: (service) =>
      (service.prices || (service as any).StagePrice || [])
        .map((price: any) => `${price.price} ${price.unitOfMeasurement?.name || price.UnitOfMeasurement?.name || ''}`.trim())
        .join('; '),
  },
  {
    id: 'costs',
    label: 'Custo',
    sortable: false,
    cell: (service) => {
      const cList = (service.costs || []).map((c: any) => ({ price: String(c.cost), unitOfMeasurement: c.unitOfMeasurement || c.UnitOfMeasurement }))
      const formatted = formatPrices(cList)
      return (
        <OverflowCell
          text={formatted}
          forceTooltip={cList.length > 1}
          maxWidth={200}
        >
          {getFirstWithEllipsis(formatted)}
        </OverflowCell>
      )
    },
    exportValue: (service) =>
      (service.costs || [])
        .map((cost: any) => `${cost.cost} ${cost.unitOfMeasurement?.name || cost.UnitOfMeasurement?.name || ''}`.trim())
        .join('; '),
  },
  {
    id: 'markup',
    label: 'Markup',
    accessor: (service) => formatMarkup(service.markup),
  },
  {
    id: 'description',
    label: 'Descrição detalhada',
    accessor: (service) => service.description || '-',
    cell: (service) => (
      <OverflowCell text={service.description || '-'} maxWidth={220}>
        {service.description || '-'}
      </OverflowCell>
    ),
  },
  {
    id: 'observation',
    label: 'Observações',
    accessor: (service) => service.observation || '-',
    cell: (service) => (
      <OverflowCell text={service.observation || '-'} maxWidth={220}>
        {service.observation || '-'}
      </OverflowCell>
    ),
  },
  {
    id: 'deadlineInDays',
    label: 'Prazo',
    accessor: (service) => formatDeadline(service.deadlineInDays),
  },
  {
    id: 'executionTime',
    label: 'Tempo execução',
    accessor: (service) => formatTime(service.executionTime),
  },
  {
    id: 'setupTime',
    label: 'Tempo setup',
    accessor: (service) => formatTime(service.setupTime),
  },
]

const StageServicesTable:React.FC = () => {
  const navigate = useNavigate()
  const context = useOutletContext<any>()
  const rawContext = context?.outletContext || context
  const search = rawContext?.search || ''
  const refresh = rawContext?.refresh
  const appliedFilters = rawContext?.appliedFilters || []

  const [services, setServices] = useState<Service[]>([])
  const [visibleCount, setVisibleCount] = useState<number>(50)
  const [totalServices, setTotalServices] = useState<number | undefined>(undefined)
  const [deleteTarget, setDeleteTarget] = useState<Service | null>(null)
  const [localRefresh, setLocalRefresh] = useState(0)
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string | number>>(new Set())
  const [bulkDraft, setBulkDraft] = useState<Record<string, any>>({})
  const [isBulkSaving, setIsBulkSaving] = useState(false)
  const [tableLoading, setTableLoading] = useState(false)
  const { setFeedback } = useContext(FeedbackContext)

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/services/service-stage/${deleteTarget.id}`)
      setFeedback({ message: 'Serviço excluído com sucesso!', isOpen: true, statusOk: true })
      setDeleteTarget(null)
      setLocalRefresh((prev) => prev + 1)
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao excluir serviço'
      setDeleteTarget(null)
      setFeedback({ message: errorMessage, isOpen: true, statusOk: false })
    }
  }

  const columns = useMemo(() => buildServicesColumns(), [])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(servicesScreenKey, columns)

  const appliedFiltersJson = useMemo(() => JSON.stringify(appliedFilters || []), [appliedFilters])

  useEffect(() => {
    let isCancelled = false
    const getServices = async () => {
      setTableLoading(true)
      try {
        const parsedFilters = appliedFiltersJson !== '[]' ? appliedFiltersJson : undefined
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/services/service-stage`, {
          params: {
            limit: visibleCount,
            offset: 0,
            search: search || undefined,
            filters: parsedFilters
          }
        })
        if (!isCancelled && response.data) {
          setServices(response.data)
        }
        try {
          const resp = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/services/service-stage/count`, {
            params: {
              search: search || undefined,
              filters: parsedFilters
            }
          })
          if (!isCancelled && resp && resp.data != null) {
            const value = typeof resp.data === 'number' ? resp.data : resp.data.totalItems ?? resp.data.total ?? undefined
            setTotalServices(value)
          }
        } catch {
          if (!isCancelled) setTotalServices(undefined)
        }
      } catch (error) {
        console.error(error)
      } finally {
        if (!isCancelled) {
          setTableLoading(false)
        }
      }
    }
    getServices()
    return () => {
      isCancelled = true
    }
  }, [visibleCount, search, refresh, appliedFiltersJson, localRefresh])


  const handleSelectOption = (colId: string, option: any) => {
    const idField = `${colId}Id`
    if (!option) {
      setBulkDraft((prev) => {
        const next = { ...prev }
        delete next[colId]
        delete next[idField]
        return next
      })
      return
    }
    const name = option.name || option.type || String(option.id)
    setBulkDraft((prev) => ({
      ...prev,
      [colId]: name,
      ...(option.id ? { [idField]: option.id } : {}),
    }))
  }

  const handleSaveBulkEdits = async () => {
    if (selectedRowIds.size === 0) return
    setIsBulkSaving(true)
    try {
      const selectedIds = Array.from(selectedRowIds)
      const payload: any = {}
      if (bulkDraft.name) payload.name = bulkDraft.name
      if (bulkDraft.code) payload.code = bulkDraft.code
      if (bulkDraft.categoryId) payload.categoryId = Number(bulkDraft.categoryId)
      if (bulkDraft.typeId) payload.typeId = Number(bulkDraft.typeId)
      if (bulkDraft.areaId) payload.areaId = Number(bulkDraft.areaId)
      if (bulkDraft.sectorId) payload.sectorId = Number(bulkDraft.sectorId)
      if (bulkDraft.markup) payload.markup = bulkDraft.markup
      if (bulkDraft.deadlineInDays) payload.deadlineInDays = Number(bulkDraft.deadlineInDays)
      if (bulkDraft.executionTime) payload.executionTime = Number(bulkDraft.executionTime)
      if (bulkDraft.setupTime) payload.setupTime = Number(bulkDraft.setupTime)

      await Promise.all(selectedIds.map((id) => ApiService.services.update(Number(id), payload)))

      setFeedback({
        message: `${selectedIds.length} serviço(s) atualizado(s) com sucesso!`,
        isOpen: true,
        statusOk: true,
      })
      setSelectedRowIds(new Set())
      setBulkDraft({})
      setLocalRefresh((prev) => prev + 1)
    } catch (error: any) {
      console.error(error)
      setFeedback({
        message: 'Erro ao processar alteração em massa.',
        isOpen: true,
        statusOk: false,
      })
    } finally {
      setIsBulkSaving(false)
    }
  }

  const renderBulkInput = (colId: string) => {
    const value = bulkDraft[colId] || ''
    const idValue = bulkDraft[`${colId}Id`]
    const selectedOption = value ? { id: idValue || value, name: value } : null

    if (colId === 'prices' || colId === 'costs') {
      return null
    }

    const isLookup = ['area', 'sector', 'category', 'type'].includes(colId)

    if (isLookup) {
      return (
        <div style={{ minWidth: '130px', maxWidth: '200px' }}>
          <InfiniteSelect
            mode="crud"
            value={selectedOption}
            placeholder="Alterar..."
            searchPlaceholder="Buscar ou criar..."
            onSelect={(option) => handleSelectOption(colId, option)}
            showPantoneSwatch={false}
            fetchFunction={async (_limit, _offset, searchVal) => {
              let response: any
              if (colId === 'area') {
                response = await ApiService.services.getAreas()
              } else if (colId === 'sector') {
                response = await ApiService.services.getSectors(bulkDraft.areaId ? { areaId: bulkDraft.areaId } : undefined)
              } else if (colId === 'category') {
                response = await ApiService.services.getCategories()
              } else if (colId === 'type') {
                response = await ApiService.services.getTypes(bulkDraft.categoryId ? { categoryId: bulkDraft.categoryId } : undefined)
              }

              const items = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : []
              const mapped = items
                .map((item: any, idx: number) => {
                  if (typeof item === 'string') return { id: item, name: item }
                  const name = item.name || item.type || ''
                  return {
                    id: item.id || name || `temp-${idx}`,
                    name: name,
                  }
                })
                .filter((item: any) => item.name && item.name.toLowerCase() !== 'null' && item.name.toLowerCase() !== 'undefined')

              return searchVal
                ? mapped.filter((item: any) => item.name?.toLowerCase().includes(searchVal.toLowerCase()))
                : mapped
            }}
            onItemCreate={async (name: string) => {
              let response: any
              const trimmedName = name.trim()
              if (colId === 'area') {
                response = await ApiService.services.createArea({ name: trimmedName })
              } else if (colId === 'sector') {
                response = await ApiService.services.createSector({ name: trimmedName, areaId: bulkDraft.areaId })
              } else if (colId === 'category') {
                response = await ApiService.services.createCategory({ name: trimmedName })
              } else if (colId === 'type') {
                response = await ApiService.services.createType({ name: trimmedName, categoryId: bulkDraft.categoryId })
              }
              const resData = response?.data || {}
              return { id: resData.id || trimmedName, name: resData.name || resData.type || trimmedName }
            }}
            onItemDelete={async (id: any, cascade?: boolean) => {
              if (colId === 'area') {
                await ApiService.services.deleteArea(id, cascade)
              } else if (colId === 'sector') {
                await ApiService.services.deleteSector(id)
              } else if (colId === 'category') {
                await ApiService.services.deleteCategory(id, cascade)
              } else if (colId === 'type') {
                await ApiService.services.deleteType(id)
              }
            }}
            allowCreate={true}
            allowDelete={true}
            allowCascadeDelete={colId === 'area' || colId === 'category'}
            cascadeDeleteLabel={
              colId === 'area'
                ? 'Excluir a área e todos os setores vinculados a ela'
                : colId === 'category'
                ? 'Excluir a categoria e todos os tipos vinculados a ela'
                : undefined
            }
            showCreateOnlyIfNotExists={true}
            minCreateLength={2}
          />
        </div>
      )
    }

    return (
      <input
        type="text"
        placeholder="Alterar..."
        value={value}
        onChange={(e) => setBulkDraft((prev) => ({ ...prev, [colId]: e.target.value }))}
        style={{
          width: '100%',
          padding: '4px 6px',
          fontSize: '11px',
          borderRadius: '4px',
          border: '1px solid #0090d3',
          backgroundColor: '#fff',
          boxSizing: 'border-box',
        }}
      />
    )
  }

  return (
    <TableWrapper>
      <StandardDataTable
        columns={columns}
        data={services}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        totalCount={typeof totalServices === 'number' ? totalServices : null}
        onLoadMore={() => setVisibleCount(prev => prev + 50)}
        hasMore={typeof totalServices === 'number' ? services.length < totalServices : false}
        isLoading={tableLoading}
        countLabel='serviços'
        exportFileName='servicos'
        emptyMessage='Nenhum serviço encontrado.'
        tableName='Stage'
        selectable={true}
        selectedRowIds={selectedRowIds}
        onSelectRow={(id, sel) => {
          setSelectedRowIds((prev) => {
            const next = new Set(prev)
            if (sel) next.add(id)
            else next.delete(id)
            return next
          })
        }}
        onSelectAllRows={(sel) => {
          if (sel) {
            setSelectedRowIds(new Set(services.map((s) => s.id)))
          } else {
            setSelectedRowIds(new Set())
            setBulkDraft({})
          }
        }}
        bulkConfig={{
          bulkDraft,
          onBulkDraftChange: (field, val) => setBulkDraft((prev) => ({ ...prev, [field]: val })),
          onSave: handleSaveBulkEdits,
          isSaving: isBulkSaving,
          selectedCountLabel: 'serviço(s)',
          onClearSelection: () => {
            setSelectedRowIds(new Set())
            setBulkDraft({})
          },
          renderBulkInput,
        }}
        rowActions={(service) => [
          { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => navigate(`view/?id=${service.id}`) },
          { label: 'Editar', icon: <Pencil size={16} />, onClick: () => navigate(`/panel/service/edit/?id=${service.id}`) },
          { label: 'Excluir', icon: <Trash2 size={16} />, onClick: () => setDeleteTarget(service) },
        ]}
      />

      <ConfirmationModal
        isOpen={!!deleteTarget}
        title="Confirmar Exclusão"
        message={
          <div>
            <p>Tem certeza que deseja excluir o serviço: &quot;{deleteTarget?.name}&quot;?</p>
            <p>Esta ação não pode ser desfeita.</p>
          </div>
        }
        actions={[
          { label: 'Cancelar', onClick: () => setDeleteTarget(null), variant: 'outline' },
          { label: 'Excluir', onClick: handleConfirmDelete, variant: 'danger' },
        ]}
      />
    </TableWrapper>
  )
}

export default StageServicesTable
